import {
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  useState,
} from 'react';

import { cn } from '@shared/lib/cn';

import {
  DEFAULT_STATE_CLASS,
  DISABLED_STATE_CLASS,
  INVALID_STATE_CLASS,
  SHAPE_CLASS,
  SIZE_CLASS,
  type TextInputShape,
  type TextInputSize,
  type TextInputStatus,
} from './text-input.constant';
import { TextInputSkeleton } from './text-input.skeleton.ui';

export interface TextInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size' | 'value' | 'defaultValue'
> {
  /** Figma `shape`. pill = 로그인 · 비밀번호 화면, plain = 테두리 · 면 없음. 기본 default */
  shape?: TextInputShape;
  /** Figma `size`(높이 sm 32 · md 40 · lg 48). 기본 md */
  size?: TextInputSize;
  /** 제어 값. `filled` 는 이 값(또는 비제어 입력값)이 비었는지로 정해진다 */
  value?: string | number;
  /** 비제어 초기값 */
  defaultValue?: string | number;
  /** Figma `state=error` */
  invalid?: boolean;
  /** Figma `status`. init 이면 스켈레톤을 그린다 */
  status?: TextInputStatus;
  /** Figma `Leading icon` + `Leading glyph`. 보통 `<Icon name="…" size="md" />` */
  leading?: ReactNode;
  /** Figma `Trailing icon` + `Trailing glyph`. 보통 `<Icon name="…" size="sm" />` 또는 아이콘 버튼 */
  trailing?: ReactNode;
  /** 바깥 상자(테두리 · 면)에 붙는다. 너비는 여기로: 기본은 부모 너비 */
  className?: string;
  /** 안쪽 `<input>` 에 붙는다 */
  inputClassName?: string;
  ref?: Ref<HTMLInputElement>;
}

/**
 * 한 줄 입력. 값은 한 줄 말줄임.
 * SearchInput · PasswordInput · NumberInput · DatePicker · Select 트리거가 이것을 감싸서 쓴다.
 */
export function TextInput({
  shape = 'default',
  size = 'md',
  value,
  defaultValue,
  onChange,
  invalid = false,
  status = 'default',
  leading,
  trailing,
  className,
  inputClassName,
  disabled,
  ref,
  ...rest
}: TextInputProps) {
  const isControlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(() => String(defaultValue ?? ''));
  const current = isControlled ? String(value) : innerValue;
  const filled = current.length > 0;

  if (status === 'init') return <TextInputSkeleton size={size} className={className} />;

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isControlled) setInnerValue(event.target.value);
    onChange?.(event);
  };

  const stateClass = disabled
    ? DISABLED_STATE_CLASS[shape]
    : invalid
      ? INVALID_STATE_CLASS[shape]
      : DEFAULT_STATE_CLASS[shape];

  return (
    <div
      data-slot="text-input"
      data-shape={shape}
      data-size={size}
      data-filled={filled}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
      className={cn(
        'flex w-full min-w-0 items-center gap-2 text-fg-default',
        SIZE_CLASS[size],
        SHAPE_CLASS[shape],
        stateClass,
        disabled && 'text-fg-disabled',
        className,
      )}
    >
      {leading ? <span className="flex shrink-0 items-center">{leading}</span> : null}
      <input
        ref={ref}
        {...rest}
        {...(isControlled ? { value } : { defaultValue })}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        onChange={handleChange}
        className={cn(
          'h-full min-w-0 flex-1 truncate bg-transparent text-fg-default outline-none typo-body placeholder:text-fg-placeholder disabled:cursor-not-allowed disabled:text-fg-disabled disabled:placeholder:text-fg-disabled',
          inputClassName,
        )}
      />
      {trailing ? <span className="flex shrink-0 items-center">{trailing}</span> : null}
    </div>
  );
}
